import {
  BookOpenText,
  Check,
  Download,
  ExternalLink,
  FileText,
  MessageSquarePlus,
} from "lucide-react";
import type { HubSearchResult } from "../lib/types";

type AssistantSourceCardProps = {
  result: HubSearchResult;
  rank: number;
  selectedForChat: boolean;
  selectionFull: boolean;
  onPreview: () => void;
  onOpen: () => void;
  onChatToggle: () => void;
};

export function AssistantSourceCard({
  result,
  rank,
  selectedForChat,
  selectionFull,
  onPreview,
  onOpen,
  onChatToggle,
}: AssistantSourceCardProps) {
  const Icon = result.type === "module" ? BookOpenText : FileText;
  const score = Math.max(0, Math.min(100, Math.round(result.score || 0)));
  const chatDisabled = result.chatAvailable === false || (selectionFull && !selectedForChat);

  return (
    <article className={["assistant-source-card", selectedForChat ? "is-chat-selected" : ""].filter(Boolean).join(" ")}>
      <button className="assistant-source-card__main" type="button" onClick={onPreview}>
        <div className="assistant-source-card__topline">
          <span className="rank-badge">#{rank}</span>
          <span className={`source-type source-type--${result.type}`}><Icon size={14} /> {typeLabel(result)}</span>
          {result.format ? <span className="format-badge">{result.format}</span> : null}
          {score > 0 ? <span className="score-badge"><strong>{score}%</strong> relevant</span> : null}
        </div>
        <h3>{result.title}</h3>
        <p className="assistant-source-card__context">
          <span>{result.sectionTitle}</span>
          {result.moduleTitle && result.moduleTitle !== result.title ? <span>{result.moduleTitle}</span> : null}
        </p>
        <p className="assistant-source-card__excerpt">{result.excerpt || fallbackExcerpt(result)}</p>
        {score > 0 ? <span className="relevance-track" aria-hidden="true"><i style={{ width: `${score}%` }} /></span> : null}
      </button>
      <div className="assistant-source-card__actions">
        <button
          className={selectedForChat ? "source-action source-action--selected" : "source-action"}
          type="button"
          onClick={onChatToggle}
          disabled={chatDisabled}
          title={result.chatAvailable === false ? "A chat brief is not available for this source" : undefined}
        >
          {selectedForChat ? <Check size={17} /> : <MessageSquarePlus size={17} />}
          <span>{selectedForChat ? "Selected" : "Add to chat"}</span>
        </button>
        <button className="source-action source-action--quiet" type="button" onClick={onOpen}>
          {result.type === "resource" ? <Download size={17} /> : <ExternalLink size={17} />}
          <span>{result.type === "resource" ? "Open file" : "Read source"}</span>
        </button>
      </div>
    </article>
  );
}

function typeLabel(result: HubSearchResult) {
  if (result.type === "module") return "Topic";
  if (result.type === "chapter") return "Chapter";
  if (result.type === "resource") return "Resource";
  return "Knowledge area";
}

function fallbackExcerpt(result: HubSearchResult) {
  if (result.type === "resource") return "Open the original public MCS resource.";
  if (result.type === "chapter") return "Read this chapter in the MCS guidance collection.";
  return "Explore this part of the Modified Cash Standard knowledge base.";
}
